/**
 * One Fingerspot machine: who is on it, what Hearth has asked it to do, and
 * its connection settings. Replaces the legacy "upload / delete employee to
 * machine", "copy user", "backup / restore" and "download scan log" screens.
 */
import { Link, useParams } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Skeleton, Switch, useConfirm, useToast } from '../components/ui.tsx';
import { ApiError, get, patch, post, qs } from '../lib/api.ts';
import { relative } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface Device {
  id: string;
  name: string;
  kind: 'fingerspot' | 'face_kiosk' | 'qr_kiosk' | 'door_lock';
  office: string;
  officeId: string;
  serialNo: string | null;
  cloudId: string | null;
  host: string | null;
  port: string | null;
  active: boolean;
  countsForAttendance: boolean;
  allowEnrollmentSharing: boolean;
  lastSeenAt: string | null;
  transport: 'cloud' | 'easylink' | null;
  /** DEVICE_COMMANDS=off on the server (a test copy): nothing is sent to machines. */
  commandsOff?: boolean;
}
interface MachineUser {
  pin: string;
  employee: { id: string; name: string; employeeNo: string | null; status: string } | null;
  nameOnMachine: string | null;
  fingerCount: number;
  hasFace: boolean;
  hasBackup: boolean;
  onMachineSince: string | null;
  pendingRemoval: boolean;
  lastJob: { kind: string; status: 'queued' | 'sent' | 'done' | 'failed'; error: string | null; at: string } | null;
}
interface Job {
  id: string;
  kind: string;
  pin: string | null;
  status: 'queued' | 'sent' | 'done' | 'failed';
  attempts: number;
  error: string | null;
  result: Record<string, unknown> | null;
  toDeviceId: string | null;
  createdAt: string;
  finishedAt: string | null;
}

const TRANSPORT = { cloud: 'Fingerspot cloud API', easylink: 'EasyLink SDK on the office network' } as const;
const JOB: Record<string, string> = { upload_user: 'Put on machine', delete_user: 'Take off machine', download_user: 'Read from machine', copy_user: 'Copy to another machine', set_privilege: 'Change privilege', backup: 'Back up', restore: 'Restore', pull_logs: 'Pull scan logs' };
const STATUS = { queued: { tone: 'wait', label: 'Queued' }, sent: { tone: 'wait', label: 'Waiting for machine' }, done: { tone: 'ok', label: 'Done' }, failed: { tone: 'bad', label: 'Failed' } } as const;

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

export function DeviceDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe();
  const admin = !!me.data?.access.devices;
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => get<Device[]>('/api/devices') });
  const dev = devices.data?.find((d) => d.id === id);
  const busy = (jobs?: Job[]) => !!jobs?.some((j) => j.status === 'queued' || j.status === 'sent');
  const jobs = useQuery({ queryKey: ['device-jobs', id], queryFn: () => get<Job[]>(`/api/devices/${id}/jobs`), refetchInterval: (q) => (busy(q.state.data) ? 4000 : false) });
  const users = useQuery({ queryKey: ['device-users', id], queryFn: () => get<{ transport: Device['transport']; sharing: boolean; users: MachineUser[] }>(`/api/devices/${id}/users`), refetchInterval: busy(jobs.data) ? 4000 : false });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [copying, setCopying] = useState(false);
  const [editing, setEditing] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['device-jobs', id] });
    void qc.invalidateQueries({ queryKey: ['device-users', id] });
  };
  const action = useMutation({
    mutationFn: (a: { path: string; body?: Record<string, unknown>; done: string }) => post<{ queued: number }>(`/api/devices/${id}${a.path}`, a.body ?? {}).then((r) => ({ ...r, done: a.done })),
    onSuccess: (r) => {
      toast({ title: r.queued ? r.done : 'Already queued' });
      setSelected(new Set());
      refresh();
    },
    onError: (e) => toast({ title: errText(e), tone: 'error' }),
  });
  const retry = useMutation({ mutationFn: (jobId: string) => post(`/api/devices/jobs/${jobId}/retry`), onSuccess: refresh, onError: (e) => toast({ title: errText(e), tone: 'error' }) });

  const list = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (users.data?.users ?? []).filter((u) => !f || u.pin.includes(f) || u.employee?.name.toLowerCase().includes(f) || u.nameOnMachine?.toLowerCase().includes(f));
  }, [users.data, filter]);
  const allOn = list.length > 0 && list.every((u) => selected.has(u.pin));
  const someOn = list.some((u) => selected.has(u.pin));
  const toggle = (pin: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(pin)) n.delete(pin);
    else n.add(pin);
    return n;
  });

  if (devices.isLoading) return <Skeleton h={420} />;
  if (!dev) return <Empty title="This machine was removed or is not in your offices." action={<Link to="/devices"><Button>All devices</Button></Link>} />;
  const t = dev.transport;
  const leavers = (users.data?.users ?? []).filter((u) => u.employee && u.employee.status !== 'active' && u.employee.status !== 'onboarding' && !u.pendingRemoval);

  return (
    <div>
      <PageHead
        eyebrow={
          <>
            <Link to="/devices" className="faint">Devices</Link> · {dev.office}
            {users.data ? ` · ${users.data.users.length} ${users.data.users.length === 1 ? 'person' : 'people'}` : ''}
          </>
        }
        title={<>{dev.name}</>}
        lede={t ? `Driven through the ${TRANSPORT[t]}. Changes are queued and sent one by one; this page updates as the machine answers.` : dev.commandsOff ? 'Commands to machines are switched off on this server. Punches still arrive; nothing is sent to the machine.' : 'Hearth cannot reach this machine yet. Add its cloud id, or its EasyLink host and serial number.'}
        aside={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {admin ? <Button icon="sliders" onClick={() => setEditing(true)}>Connection</Button> : null}
            <Button variant="primary" icon="plus" disabled={!t} onClick={() => setAdding(true)}>Add people</Button>
          </div>
        }
      />
      <Reveal className="stack">
        {!t ? (
          <Reveal.Item>
            {dev.commandsOff ? (
              <Callout tone="info" title="Commands are off">This server runs with DEVICE_COMMANDS=off (a test copy of production data), so it never contacts machines. The connection details below are kept as they are.</Callout>
            ) : (
              <Callout tone="warn" title="Not connected">A machine with a cloud id needs FINGERSPOT_API_TOKEN on the server. A machine on the office network needs the EasyLink SDK host, port and the machine’s serial number.</Callout>
            )}
          </Reveal.Item>
        ) : null}
        {leavers.length ? (
          <Reveal.Item>
            <Callout tone="warn" title={`${leavers.length} ${leavers.length === 1 ? 'person has' : 'people have'} left but ${leavers.length === 1 ? 'is' : 'are'} still on this machine`}>
              <div className="row row--wrap row--between" style={{ gap: 12 }}>
                <span>{leavers.slice(0, 4).map((u) => u.employee!.name).join(', ')}{leavers.length > 4 ? ` and ${leavers.length - 4} more` : ''}. The nightly sweep takes leavers off unless it is switched off in Settings.</span>
                <Button size="sm" variant="danger" disabled={!t} onClick={() => action.mutate({ path: '/users/remove', body: { pins: leavers.map((u) => u.pin) }, done: 'Removal queued' })}>Take them off now</Button>
              </div>
            </Callout>
          </Reveal.Item>
        ) : null}
        <Reveal.Item className="grid-12">
          <div className="col-8">
            <Panel
              eyebrow="On the machine"
              title="People"
              action={
                <div className="input-group" style={{ maxWidth: 220 }}>
                  <Icon name="search" />
                  <input className="input" placeholder="Name or PIN" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter people on this machine" />
                </div>
              }
              flush
            >
              {users.isLoading ? (
                <div style={{ padding: 20 }}><Skeleton h={220} /></div>
              ) : !list.length ? (
                <Empty title={filter ? 'No one matches.' : 'No one on this machine yet.'}>{filter ? null : 'Add people, or back up the machine to read who is already on it.'}</Empty>
              ) : (
                <div className="table-wrap" style={{ margin: 0, padding: '0 var(--s-5)' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}>
                          <input
                            type="checkbox"
                            className="check"
                            checked={allOn}
                            ref={(el) => {
                              if (el) el.indeterminate = someOn && !allOn;
                            }}
                            onChange={() => setSelected(allOn ? new Set() : new Set(list.map((u) => u.pin)))}
                            aria-label="Select everyone shown"
                          />
                        </th>
                        <th>PIN</th>
                        <th>Person</th>
                        <th className="hide-sm">Enrolled</th>
                        <th>State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((u) => (
                        <tr key={u.pin}>
                          <td>
                            <input type="checkbox" className="check" checked={selected.has(u.pin)} onChange={() => toggle(u.pin)} aria-label={`Select PIN ${u.pin}`} />
                          </td>
                          <td className="mono">{u.pin}</td>
                          <td>
                            {u.employee ? (
                              <Link to="/people/$id" params={{ id: u.employee.id }} className="row-link">{u.employee.name}</Link>
                            ) : (
                              <span className="faint">{u.nameOnMachine ?? 'Unknown'} · no employee</span>
                            )}
                            {u.employee && u.employee.status !== 'active' ? <span className="faint" style={{ fontSize: 'var(--step--2)' }}> · {u.employee.status}</span> : null}
                          </td>
                          <td className="hide-sm faint nowrap">
                            {u.fingerCount ? `${u.fingerCount} finger${u.fingerCount === 1 ? '' : 's'}` : 'No fingers'}
                            {u.hasFace ? ' · face' : ''}
                            {u.hasBackup ? <span title="Templates are backed up, encrypted"> · <Icon name="lock" width={12} height={12} style={{ display: 'inline', verticalAlign: '-1px' }} /></span> : null}
                          </td>
                          <td><UserState u={u} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
            {selected.size ? (
              <div className="select-bar" role="toolbar" aria-label="Selected people">
                <span className="faint" style={{ fontSize: 'var(--step--1)' }}>{selected.size} selected</span>
                <Button size="sm" icon="arrowUpRight" disabled={!t} onClick={() => setCopying(true)}>Copy to…</Button>
                <Button
                  size="sm"
                  variant="danger"
                  icon="x"
                  disabled={!t}
                  onClick={async () => {
                    const ok = await confirm.ask(`Take ${selected.size} off ${dev.name}?`, 'They will no longer be able to clock in (or open the door) on this machine. Their encrypted template backup for it is deleted once the machine confirms.', 'Take off', 'danger');
                    if (ok) action.mutate({ path: '/users/remove', body: { pins: [...selected] }, done: 'Removal queued' });
                  }}
                >
                  Take off
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
              </div>
            ) : null}
          </div>
          <div className="col-4 stack">
            <Panel eyebrow="Machine" title="Maintenance">
              <div className="stack" style={{ ['--gap' as string]: '10px' }}>
                <MaintenanceRow title="Back up" body="Read everyone on the machine, with their templates, into an encrypted backup." disabled={!t || !admin} onClick={() => action.mutate({ path: '/backup', done: 'Backup queued' })} />
                <MaintenanceRow
                  title="Restore"
                  body="Put everyone in the backup back on, e.g. after the machine was replaced or reset."
                  disabled={!t || !admin}
                  onClick={async () => {
                    if (await confirm.ask(`Restore ${dev.name}?`, 'Everyone listed here is uploaded to the machine again, with their backed-up templates where the format matches.', 'Restore')) action.mutate({ path: '/restore', done: 'Restore queued' });
                  }}
                />
                <MaintenanceRow title="Pull scan logs" body={t === 'cloud' ? 'Fetch the last two days of punches in case a webhook was missed.' : 'Fetch punches the machine has not sent yet.'} disabled={!t} onClick={() => action.mutate({ path: '/pull-logs', done: 'Pull queued' })} />
              </div>
            </Panel>
            <Panel eyebrow="Connection" title={t ? TRANSPORT[t] : dev.commandsOff ? 'Commands off' : 'Not connected'}>
              <dl className="dl" style={{ gridTemplateColumns: '96px 1fr' }}>
                <dt>Serial</dt>
                <dd className="mono">{dev.serialNo ?? '—'}</dd>
                <dt>Cloud ID</dt>
                <dd className="mono">{dev.cloudId ?? '—'}</dd>
                <dt>EasyLink</dt>
                <dd className="mono">{dev.host ? `${dev.host}:${dev.port ?? 80}` : '—'}</dd>
                <dt>Sharing</dt>
                <dd>{dev.allowEnrollmentSharing ? 'Others may copy from it' : 'Off'}</dd>
                <dt>Last seen</dt>
                <dd>{dev.lastSeenAt ? relative(dev.lastSeenAt) : 'Never'}</dd>
              </dl>
            </Panel>
          </div>
        </Reveal.Item>
        <Reveal.Item>
          <Panel eyebrow="Queue" title="What Hearth asked the machine" flush>
            {!jobs.data?.length ? (
              <Empty title="Nothing yet." />
            ) : (
              <div className="table-wrap" style={{ margin: 0, padding: '0 var(--s-5)' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>PIN</th>
                      <th>State</th>
                      <th className="hide-sm">Outcome</th>
                      <th className="right">When</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.data.slice(0, 60).map((j) => (
                      <tr key={j.id}>
                        <td>
                          {JOB[j.kind] ?? j.kind}
                          {j.toDeviceId ? <span className="faint"> → {devices.data?.find((d) => d.id === j.toDeviceId)?.name ?? 'another machine'}</span> : null}
                        </td>
                        <td className="mono">{j.pin ?? '—'}</td>
                        <td>
                          <Chip tone={STATUS[j.status].tone} live={j.status === 'sent'}>{STATUS[j.status].label}</Chip>
                          {j.attempts > 1 ? <span className="faint" style={{ fontSize: 'var(--step--2)' }}> · try {j.attempts}</span> : null}
                        </td>
                        <td className="hide-sm" style={{ fontSize: 'var(--step--1)', color: j.error ? 'var(--bad)' : undefined }}>{j.error ?? outcome(j)}</td>
                        <td className="right faint nowrap">{relative(j.finishedAt ?? j.createdAt)}</td>
                        <td className="right">{j.status === 'failed' ? <Button size="sm" variant="ghost" icon="refresh" loading={retry.isPending && retry.variables === j.id} onClick={() => retry.mutate(j.id)}>Retry</Button> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </Reveal.Item>
      </Reveal>
      {confirm.node}
      <AddPeople open={adding} onClose={() => setAdding(false)} device={dev} onQueued={refresh} />
      <CopyTo open={copying} onClose={() => setCopying(false)} from={dev} pins={[...selected]} devices={devices.data ?? []} onQueued={() => { setSelected(new Set()); refresh(); }} />
      {admin ? <EditConnection open={editing} onClose={() => setEditing(false)} device={dev} /> : null}
    </div>
  );
}

function UserState({ u }: { u: MachineUser }) {
  if (u.lastJob?.status === 'failed') return <span title={u.lastJob.error ?? undefined}><Chip tone="bad">{JOB[u.lastJob.kind] ?? 'Request'} failed</Chip></span>;
  if (u.pendingRemoval) return <Chip tone="wait" live>Coming off</Chip>;
  if (u.lastJob && (u.lastJob.status === 'queued' || u.lastJob.status === 'sent')) return <Chip tone="wait" live>{u.lastJob.kind === 'upload_user' ? 'Going on' : 'Working'}</Chip>;
  if (u.onMachineSince) return <Chip tone="ok">On machine</Chip>;
  return <Chip tone="plain">Not confirmed</Chip>;
}

function outcome(j: Job) {
  const r = j.result ?? {};
  if (j.kind === 'backup' && typeof r.stored === 'number') return `${r.stored} read${Array.isArray(r.unknownPins) && r.unknownPins.length ? `, ${r.unknownPins.length} match no employee` : ''}`;
  if (j.kind === 'backup' && typeof r.pins === 'number') return `${r.pins} PINs on the machine; reading each`;
  if (j.kind === 'restore' && typeof r.queued === 'number') return `${r.queued} queued`;
  if (j.kind === 'pull_logs' && typeof r.received === 'number') return `${r.received} punches, ${r.recorded} new${Array.isArray(r.unknownPins) && r.unknownPins.length ? `, ${r.unknownPins.length} unknown PIN${r.unknownPins.length === 1 ? '' : 's'}` : ''}`;
  if (typeof r.templates === 'string') return `Templates: ${r.templates}`;
  return j.status === 'sent' ? 'Sent; the machine answers on its webhook' : '';
}

function MaintenanceRow({ title, body, disabled, onClick }: { title: string; body: string; disabled?: boolean; onClick: () => void }) {
  return (
    <div className="row row--between" style={{ gap: 14, alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontWeight: 500 }}>{title}</div>
        <div className="faint" style={{ fontSize: 'var(--step--1)' }}>{body}</div>
      </div>
      <Button size="sm" disabled={disabled} onClick={onClick} aria-label={title}>
        <Icon name="arrowRight" width={14} height={14} />
      </Button>
    </div>
  );
}

function AddPeople({ open, onClose, device, onQueued }: { open: boolean; onClose: () => void; device: Device; onQueued: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!open) {
      setQ('');
      setPicked(new Map());
    }
  }, [open]);
  const people = useQuery({
    queryKey: ['device-add-people', device.officeId, debounced],
    queryFn: () => get<{ items: { id: string; name: string; employeeNo: string | null; jobPosition: string | null }[] }>(`/api/people${qs({ q: debounced || undefined, officeId: debounced ? undefined : device.officeId, status: 'active', size: 40 })}`),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  const m = useMutation({
    mutationFn: () => post<{ queued: number; skipped: { employeeId: string; reason: string }[] }>(`/api/devices/${device.id}/users`, { employeeIds: [...picked.keys()] }),
    onSuccess: (r) => {
      onClose();
      onQueued();
      toast({ title: `${r.queued} queued for ${device.name}`, body: r.skipped.length ? `${r.skipped.length} skipped: ${[...new Set(r.skipped.map((s) => s.reason))].join('; ')}` : 'Each person then enrols a finger or face on the machine.' });
    },
    onError: (e) => toast({ title: errText(e), tone: 'error' }),
  });
  const toggle = (id: string, name: string) => setPicked((p) => {
    const n = new Map(p);
    if (n.has(id)) n.delete(id);
    else n.set(id, name);
    return n;
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={device.name}
      title={<>Add people to the <em className="serif-italic">machine</em></>}
      width={560}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto', fontSize: 'var(--step--1)' }}>{picked.size ? `${picked.size} selected` : 'Their employee number becomes their PIN.'}</span>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!picked.size} loading={m.isPending} onClick={() => m.mutate()}>Put on machine</Button>
        </>
      }
    >
      <div className="stack">
        <div className="input-group">
          <Icon name="search" />
          <input className="input" autoFocus placeholder={`Search all staff, or pick from ${device.office}`} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
        </div>
        <div style={{ maxHeight: 340, overflowY: 'auto', margin: '0 -4px' }}>
          {people.isLoading ? <Skeleton h={180} /> : !people.data?.items.length ? <Empty title="No active staff match." /> : people.data.items.map((p) => (
            <label key={p.id} className="row" style={{ gap: 12, padding: '9px 4px', borderBottom: '1px solid var(--line-1)', cursor: 'pointer' }}>
              <input type="checkbox" className="check" checked={picked.has(p.id)} onChange={() => toggle(p.id, p.name)} disabled={!p.employeeNo} />
              <span style={{ flex: 1 }}>
                {p.name}
                <span className="faint" style={{ fontSize: 'var(--step--2)' }}>{p.jobPosition ? ` · ${p.jobPosition}` : ''}</span>
              </span>
              <span className="mono faint" style={{ fontSize: 'var(--step--1)' }}>{p.employeeNo ?? 'no number'}</span>
            </label>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function CopyTo({ open, onClose, from, pins, devices, onQueued }: { open: boolean; onClose: () => void; from: Device; pins: string[]; devices: Device[]; onQueued: () => void }) {
  const toast = useToast();
  const targets = devices.filter((d) => d.id !== from.id && d.transport && (d.kind === 'fingerspot' || d.kind === 'door_lock'));
  const [to, setTo] = useState('');
  const m = useMutation({
    mutationFn: () => post<{ queued: number }>(`/api/devices/${from.id}/users/copy`, { pins, toDeviceId: to || targets[0]?.id }),
    onSuccess: (r) => {
      onClose();
      onQueued();
      toast({ title: `${r.queued} copy request${r.queued === 1 ? '' : 's'} queued` });
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={`${pins.length} from ${from.name}`}
      title={<>Copy to another <em className="serif-italic">machine</em></>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!targets.length || !from.allowEnrollmentSharing} loading={m.isPending} onClick={() => m.mutate()}>Copy</Button>
        </>
      }
    >
      <div className="stack">
        {!from.allowEnrollmentSharing ? <Callout tone="warn">{from.name} does not allow copying its enrolments. A device admin can turn sharing on under Connection.</Callout> : null}
        <p className="muted">Fingerprints travel with the copy when both machines use the same connection. Between a cloud and an EasyLink machine only the person is copied; they enrol again on the new machine.</p>
        <Field label="Copy to">
          {(p) => (
            <select {...p} className="select" value={to || targets[0]?.id || ''} onChange={(e) => setTo(e.target.value)}>
              {targets.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.office}</option>)}
            </select>
          )}
        </Field>
        {m.error ? <Callout tone="bad">{errText(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}

function EditConnection({ open, onClose, device }: { open: boolean; onClose: () => void; device: Device }) {
  const qc = useQueryClient();
  const toast = useToast();
  const lookups = useLookups();
  const initial = () => ({ name: device.name, officeId: device.officeId, serialNo: device.serialNo ?? '', cloudId: device.cloudId ?? '', host: device.host ?? '', port: device.port ?? '', countsForAttendance: device.countsForAttendance, allowEnrollmentSharing: device.allowEnrollmentSharing, active: device.active });
  const [f, setF] = useState(initial);
  useEffect(() => {
    if (open) setF(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device.id]);
  const m = useMutation({
    mutationFn: () => patch(`/api/devices/${device.id}`, { ...f, serialNo: f.serialNo.trim() || null, cloudId: f.cloudId.trim() || null, host: f.host.trim() || null, port: f.port ? Number(f.port) : null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      void qc.invalidateQueries({ queryKey: ['device-users', device.id] });
      onClose();
      toast({ title: 'Machine updated' });
    },
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={open} onClose={onClose} eyebrow={device.name} title="Connection" width={620} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-6">
          <Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => set('name', e.target.value)} />}</Field>
        </div>
        <div className="col-6">
          <Field label="Office">
            {(p) => (
              <select {...p} className="select" value={f.officeId} onChange={(e) => set('officeId', e.target.value)}>
                {lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
          </Field>
        </div>
        <div className="col-6">
          <Field label="Serial number" hint="Printed on the machine; EasyLink needs it">{(p) => <input {...p} className="input input--mono" value={f.serialNo} onChange={(e) => set('serialNo', e.target.value)} />}</Field>
        </div>
        <div className="col-6">
          <Field label="Cloud ID" hint="Fingerspot developer portal">{(p) => <input {...p} className="input input--mono" value={f.cloudId} onChange={(e) => set('cloudId', e.target.value)} />}</Field>
        </div>
        <div className="col-8">
          <Field label="EasyLink host" hint="The office PC running the SDK. Keep it on the VPN, not the open internet.">{(p) => <input {...p} className="input input--mono" value={f.host} onChange={(e) => set('host', e.target.value)} placeholder="10.20.1.15" />}</Field>
        </div>
        <div className="col-4">
          <Field label="Port">{(p) => <input {...p} className="input input--mono" inputMode="numeric" value={f.port} onChange={(e) => set('port', e.target.value.replace(/\D/g, ''))} placeholder="8080" />}</Field>
        </div>
        <div className="col-12 stack" style={{ ['--gap' as string]: '12px' }}>
          <Switch checked={f.countsForAttendance} onChange={(v) => set('countsForAttendance', v)} label="Counts for attendance (off for door locks)" showLabel />
          <Switch checked={f.allowEnrollmentSharing} onChange={(v) => set('allowEnrollmentSharing', v)} label="Others may copy enrolments from this machine" showLabel />
          <Switch checked={f.active} onChange={(v) => set('active', v)} label="Active" showLabel />
        </div>
        {m.error ? <div className="col-12"><Callout tone="bad">{errText(m.error)}</Callout></div> : null}
      </div>
    </Dialog>
  );
}
